// SubscriptionsPage.jsx
import React, { useState, useMemo, useEffect } from "react";
import {
  Box, Typography, Chip, Stack, Button,
  TextField, InputAdornment, Dialog,
  DialogTitle, DialogContent, DialogActions,
  Grid, Select, MenuItem as MuiMenuItem,
  FormControl, InputLabel, LinearProgress,
  IconButton, Tooltip, Alert, Snackbar,
} from "@mui/material";
import SearchIcon        from "@mui/icons-material/Search";
import AddIcon           from "@mui/icons-material/Add";
import EditOutlinedIcon  from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { formatWithTzLabel } from "../../utils/dateUtils";
import AppTable from "../../common/app-table";
import AppDropdown from "../../common/app-dropdown";
import CustomTabs from "../../common/custom-tabs";
import { useAuth } from "../../hooks/AuthProvider";
import { SUBSCRIPTIONS_ENDPOINT } from "../../config/urls";
import ButtonTwo from "../../common/dashboard-button";
import ButtonOne from "../../common/normal-button";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import {
  resolveSearchQueryForApi,
  normalizeCombinedSearchInput,
  PHONE_SEARCH_ERROR_MESSAGE,
} from "../../utils/phoneSearch";
import AppChip from "../../common/app-chip";

// ── Inline constants ────────────────────────────────────────────────────────
const NAVY   = "#08245C";
const BLUE   = "#1B76D1";
const GREEN  = "#2DC77A";
const RED    = "#F04438";
const ORANGE = "#F79009";
const PURPLE = "#7C3AED";

const typography = {
  bodyLarge: { fontSize: "14px", fontFamily: "DM Sans, sans-serif" },
  h1: { fontSize: "24px", fontWeight: 700, fontFamily: "DM Sans, sans-serif" },
  h2: { fontSize: "14px", fontWeight: 400, fontFamily: "DM Sans, sans-serif" },
};

const STATUS_TABS = [
  { label: "All",      value: "all" },
  { label: "Active",   value: "active" },
  { label: "Trial",    value: "trial" },
  { label: "Expired",  value: "expired" },
  { label: "Cancelled", value: "cancelled" },
];

const PLAN_OPTIONS = [
  { label: "Monthly", value: "monthly" },
  { label: "Quarterly", value: "quarterly" },
  { label: "Yearly", value: "yearly" }
];

const STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Trial", value: "trial" },
  { label: "Expired", value: "expired" },
  { label: "Cancelled", value: "cancelled" }
];

// ── Helper components ────────────────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const map = {
    active:   { label: "Active",   bg: "#ECFDF5", color: "#047857" },
    trial:    { label: "Trial",    bg: "#FFF7ED", color: "#C2410C" },
    expired:  { label: "Expired",  bg: "#FEF2F2", color: "#B91C1C" },
    cancelled: { label: "Cancelled", bg: "#F3F4F6", color: "#6B7280" },
  };
  const s = map[status?.toLowerCase()] || map.cancelled;
  return (
    <Chip label={s.label} size="small"
      sx={{ background: s.bg, color: s.color, fontWeight: 700, fontSize: "11px", borderRadius: "20px", height: 24 }} />
  );
};

const PlanChip = ({ plan }) => {
  const color = { monthly: BLUE, quarterly: NAVY, yearly: PURPLE }[plan?.toLowerCase()] || NAVY;
  const label = plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : "—";
  return (
    <Typography sx={{ ...typography.bodyLarge, fontWeight: 700, color }}>{label}</Typography>
  );
};

const TrialCell = ({ row }) => {
  if (!row.start_date || !row.end_date) {
    return (
      <Typography sx={{ ...typography.bodyLarge, color: "#9CA3AF" }}>N/A</Typography>
    );
  }

  const start = new Date(row.start_date);
  const end = new Date(row.end_date);
  const today = new Date();
  
  // 1. Keep the original totalDays calculation based on the exact timestamps
  const totalDays = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
  
  // 2. Normalize 'today' and 'end' to exactly midnight to measure pure calendar days
  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const endDate = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  // 3. Calculate days left inclusively
  let daysLeft = 0;
  if (endDate >= todayDate) {
    // Difference in calendar days + 1 (e.g., 20th - 19th = 1. 1 + 1 = 2 days left)
    daysLeft = Math.round((endDate - todayDate) / (1000 * 60 * 60 * 24)) + 1;
  }
  
  // 4. Derive daysUsed so it perfectly syncs with the daysLeft
  let daysUsed = totalDays - daysLeft + 1;
  
  // Clamp the value to ensure the UI doesn't break (e.g., doesn't say Day 0/6 or Day 7/6)
  daysUsed = Math.max(1, Math.min(daysUsed, totalDays)); 
  
  // Only show trial progress for trial status
  if (row.status?.toLowerCase() !== "trial") {
    return (
      <Typography sx={{ ...typography.bodyLarge, color: row.status === "active" ? "#6B7280" : "#9CA3AF" }}>
        {row.status === "active" ? "Completed ✓" : "N/A"}
      </Typography>
    );
  }
  
  const pct = totalDays > 0 ? Math.round((daysUsed / totalDays) * 100) : 0;
  
  // Color logic: Red if 1 day left, Orange if 2-3 days, Green otherwise
  const barColor = daysLeft <= 1 ? RED : daysLeft <= 3 ? ORANGE : GREEN;
  
  return (
    <Box sx={{ minWidth: 140 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
        {/* <Typography sx={{ fontSize: "12px", fontWeight: 600, color: ORANGE }}>
          Day {daysUsed}/{totalDays}
        </Typography> */}
        <Typography sx={{ fontSize: "11px", fontWeight: 600, color: daysLeft <= 1 ? RED : ORANGE }}>
          {daysLeft}d left
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate" value={pct}
        sx={{
          height: 5, borderRadius: 3,
          bgcolor: "#E5E7EB",
          "& .MuiLinearProgress-bar": { bgcolor: barColor, borderRadius: 3 },
        }}
      />
    </Box>
  );
};

const fmtDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const fmtDateTime = (d) => {
  if (!d) return "—";
  return formatWithTzLabel(d, "DD MMM YYYY, hh:mm A");
};

// ── Stat Card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, accentColor, sub }) => (
  <Box
    sx={{
      background: "#fff", border: "1px solid #E5E7EB", borderRadius: "14px",
      p: "18px 20px", flex: 1, position: "relative", overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
      "&::before": {
        content: '""', position: "absolute", top: 0, left: 0, right: 0,
        height: "3px", background: accentColor,
      },
    }}
  >
    <Typography sx={{ fontSize: "11px", fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: "28px", fontWeight: 700, color: NAVY, lineHeight: 1, mt: 1, fontFamily: "DM Mono, monospace" }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: "12px", color: "#6B7280", mt: 0.5 }}>{sub}</Typography>
  </Box>
);

// ── Edit Status Dialog ─────────────────────────────────────────────────────
const EditStatusDialog = ({ open, onClose, onSave, currentStatus }) => {
  const [status, setStatus] = useState(currentStatus);

  React.useEffect(() => {
    if (open) setStatus(currentStatus);
  }, [open, currentStatus]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}>
      <DialogTitle sx={{ fontSize: "18px", fontWeight: 700, color: NAVY }}>
        Update Subscription Status
      </DialogTitle>
      <DialogContent>
        <FormControl fullWidth size="small" sx={{ mt: 2 }}>
          <InputLabel>Status</InputLabel>
          <Select 
            value={status} 
            label="Status" 
            onChange={(e) => setStatus(e.target.value)}
            sx={{ borderRadius: "8px", ...typography.bodyLarge }}
          >
            {STATUS_OPTIONS.map((o) => (
              <MuiMenuItem key={o.value} value={o.value} sx={typography.bodyLarge}>
                {o.label}
              </MuiMenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined"
          sx={{ borderRadius: "8px", textTransform: "none", ...typography.bodyLarge, fontWeight: 600, height: 40, borderColor: "#E5E7EB", color: "#1F2937" }}>
          Cancel
        </Button>
        <Button onClick={() => onSave(status)} variant="contained"
          sx={{ borderRadius: "8px", textTransform: "none", ...typography.bodyLarge, fontWeight: 600, height: 40, background: BLUE, "&:hover": { background: "#1560b0" } }}>
          Update Status
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Delete confirm dialog ─────────────────────────────────────────────────────
const ConfirmDialog = ({ open, name, onClose, onConfirm }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
    PaperProps={{ sx: { borderRadius: "16px", p: 1, textAlign: "center" } }}>
    <DialogContent sx={{ pt: 3 }}>
      <Box sx={{ width: 56, height: 56, borderRadius: "50%", background: "#FEF2F2", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
        <DeleteOutlineIcon sx={{ color: RED, fontSize: 28 }} />
      </Box>
      <Typography sx={{ fontSize: "18px", fontWeight: 700, color: NAVY, mb: 1 }}>Remove Subscriber?</Typography>
      <Typography sx={{ ...typography.bodyLarge, color: "#6B7280" }}>
        Remove "{name}"? This action cannot be undone.
      </Typography>
    </DialogContent>
    <DialogActions sx={{ justifyContent: "center", pb: 2.5, gap: 1 }}>
      <Button onClick={onClose} variant="outlined"
        sx={{ borderRadius: "8px", textTransform: "none", ...typography.bodyLarge, fontWeight: 600, height: 40, borderColor: "#E5E7EB", color: "#1F2937" }}>
        Cancel
      </Button>
      <Button onClick={onConfirm} variant="contained"
        sx={{ borderRadius: "8px", textTransform: "none", ...typography.bodyLarge, fontWeight: 600, height: 40, background: RED, "&:hover": { background: "#c0392b" } }}>
        Yes, Remove
      </Button>
    </DialogActions>
  </Dialog>
);

// ── Main Component ─────────────────────────────────────────────────────────────
const SubscriptionsPage = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [allSubscribers, setAllSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  
  const [tabValue, setTabValue]       = useState("all");
  const [search, setSearch]           = useState("");
  const [planFilter, setPlanFilter]   = useState("");
  const [page, setPage]               = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount]   = useState(0);

  const [statusEditOpen, setStatusEditOpen] = useState(false);
  const [statusEditTarget, setStatusEditTarget] = useState(null);

  const resolvedSubscriptionSearch = useMemo(
    () => resolveSearchQueryForApi(search),
    [search],
  );

  const { getValidToken } = useAuth();

  // ── API Functions ──────────────────────────────────────────────────────────
  const fetchSubscriptions = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");
      
      // Build query params
      const params = new URLSearchParams({
        offset: page * rowsPerPage, 
        limit: rowsPerPage,
      });
      
      if (tabValue !== "all") {
        params.append("status", tabValue);
      }
      
      if (planFilter) {
        params.append("plan_type", planFilter);
      }

      if (resolvedSubscriptionSearch.blockFetch) {
        setLoading(false);
        return;
      }
      if (resolvedSubscriptionSearch.apiSearchQuery) {
        params.append("search_query", resolvedSubscriptionSearch.apiSearchQuery);
      }
      
      const response = await fetch(
        `${SUBSCRIPTIONS_ENDPOINT}?${params.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      if (!response.ok) throw new Error("Failed to fetch subscriptions");
      
      const data = await response.json();
      setSubscribers(data.results || []);
      setTotalCount(data.count || 0);
    } catch (err) {
      console.error("Error fetching subscriptions:", err);
      setError(err.message);
      setSnackbar({
        open: true,
        message: `Error: ${err.message}`,
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAllSubscriptionsForStats = async () => {
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const response = await fetch(
        `${SUBSCRIPTIONS_ENDPOINT}?limit=10000`, // no status filter
        {
          headers: {
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to fetch stats");

      const data = await response.json();
      setAllSubscribers(data.results || []);
    } catch (err) {
      console.error("Stats fetch error:", err);
    }
  };

  // Fetch on initial load and when filters change
  useEffect(() => {
    fetchSubscriptions();
  }, [page, rowsPerPage, tabValue, planFilter, search]);

  useEffect(() => {
    fetchAllSubscriptionsForStats();
  }, []);

  // ── Derived stats ──────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const active = allSubscribers.filter((s) => s.status === "active").length;
    const trial = allSubscribers.filter((s) => s.status === "trial").length;
    const expired = allSubscribers.filter((s) => s.status === "expired").length;
    const cancelled = allSubscribers.filter((s) => s.status === "cancelled").length;

    return {
      total: allSubscribers.length,
      active,
      trial,
      expired,
      cancelled,
      inactive: expired + cancelled,
    };
  }, [allSubscribers]);

  // ── Export Function ────────────────────────────────────────────────────────
  const handleExport = async () => {
    if (resolvedSubscriptionSearch.blockFetch) {
      setSnackbar({
        open: true,
        message: PHONE_SEARCH_ERROR_MESSAGE,
        severity: "error",
      });
      return;
    }
    try {

      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");
      
      // Build query params for export (get all data, not just current page)
      const params = new URLSearchParams({
        limit: 10000, // Get a large number of records
      });
      
      if (tabValue !== "all") {
        params.append("status", tabValue);
      }
      
      if (planFilter) {
        params.append("plan_type", planFilter);
      }
      
      if (resolvedSubscriptionSearch.apiSearchQuery) {
        params.append("search", resolvedSubscriptionSearch.apiSearchQuery);
      }
      
      const response = await fetch(
        `${SUBSCRIPTIONS_ENDPOINT}?${params.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Tenant-Id": tenant,
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      if (!response.ok) throw new Error("Failed to fetch data for export");
      
      const data = await response.json();
      const exportData = data.results || subscribers; // Fallback to current data if API fails
      
      if (exportData.length === 0) {
        setSnackbar({
          open: true,
          message: "No data to export",
          severity: "warning"
        });
        return;
      }

      // Define CSV headers
      const headers = [
        "Name",
        "Phone",
        "Email",
        "Plan",
        "Status",
        "Start Date",
        "End Date",
        "Subscription ID",
        "Created At",
        "Updated At"
      ];

      // Transform data to CSV rows
      const rows = exportData.map((row) => [
        row.customer_name || "",
        row.customer_phone || "",
        row.customer_email || "",
        row.plan_type || "",
        row.status || "",
        row.start_date ? fmtDateTime(row.start_date) : "",
        row.end_date ? fmtDateTime(row.end_date) : "",
        row.razorpay_subscription_id || "",
        row.created_at ? fmtDateTime(row.created_at) : "",
        row.updated_at ? fmtDateTime(row.updated_at) : "",
      ]);

      // Create CSV content
      const csvContent = [
        headers.join(","),
        ...rows.map(row => 
          row.map(cell => {
            // Escape commas and quotes in cell content
            if (typeof cell === "string" && (cell.includes(",") || cell.includes('"'))) {
              return `"${cell.replace(/"/g, '""')}"`;
            }
            return cell;
          }).join(",")
        )
      ].join("\n");

      // Create and download CSV file
      const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" }); // Add BOM for UTF-8
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      
      // Generate filename with current date and filters
      const date = new Date().toISOString().split("T")[0];
      const statusSuffix = tabValue !== "all" ? `_${tabValue}` : "";
      const planSuffix = planFilter ? `_${planFilter}` : "";
      link.setAttribute("download", `subscriptions_${date}${statusSuffix}${planSuffix}.csv`);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: `Exported ${exportData.length} records successfully`,
        severity: "success"
      });
    } catch (err) {
      console.error("Error exporting data:", err);
      setSnackbar({
        open: true,
        message: `Export failed: ${err.message}`,
        severity: "error"
      });
    }
  };

  // ── Status Update Handler ──────────────────────────────────────────────────
  const handleStatusUpdate = async (newStatus) => {
    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");
      
      const response = await fetch(`${SUBSCRIPTIONS_ENDPOINT}${statusEditTarget.id}/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      
      if (!response.ok) throw new Error("Failed to update status");
      
      setSnackbar({
        open: true,
        message: "Status updated successfully",
        severity: "success"
      });
      
      setStatusEditOpen(false);
      setStatusEditTarget(null);
      fetchSubscriptions(); // Refresh list
    } catch (err) {
      console.error("Error updating status:", err);
      setSnackbar({
        open: true,
        message: `Error: ${err.message}`,
        severity: "error"
      });
    }
  };

  const onRowsPerPageChange = (e) => {
    const val = Number(e?.target?.value);
    if (!val || isNaN(val)) return;
    setRowsPerPage(val);
    setPage(0);
  };

  // ── Column definitions ────────────────────────────────────────────────────────

  const columns = [
    {
      id: "customer_name", 
      label: "Name", 
      minWidth: 140,
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, fontWeight: 600, color: NAVY }}>
          {row.customer_name || "—"}
        </Typography>
      ),
    },
    {
      id: "customer_phone", 
      label: "Phone", 
      minWidth: 120,
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#6B7280" }}>
          {row.customer_phone || "—"}
        </Typography>
      ),
    },
    {
      id: "customer_email", 
      label: "Email", 
      minWidth: 160,
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#6B7280" }}>
          {row.customer_email || "—"}
        </Typography>
      ),
    },
    {
      id: "customer_date_of_birth",
      label: "Date of Birth",
      minWidth: 120,
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#6B7280" }}>
          {fmtDate(row.customer_date_of_birth)}
        </Typography>
      ),
    },
    {
      id: "customer_gender",
      label: "Gender",
      minWidth: 100,
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#6B7280" }}>
          {row.customer_gender || "—"}
        </Typography>
      ),
    },
    {
      id: "customer_birth_city",
      label: "Birth City",
      minWidth: 120,
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#6B7280" }}>
          {row.customer_birth_city || "—"}
        </Typography>
      ),
    },
    {
      id: "customer_time_of_birth",
      label: "Time of Birth",
      minWidth: 120,
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#6B7280" }}>
          {row.customer_time_of_birth || "—"}
        </Typography>
      ),
    },
    {
      id: "plan_type",
      label: "Plan",
      minWidth: 100,
      render: (row) => (
        <AppChip
          label={row.plan_type || "—"}
          color={row.plan_type === "premium" ? "primary" : "default"}
          variant="outlined"
        />
      ),
    },
    {
      id: "status", 
      label: "Status", 
      minWidth: 120,
      render: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <StatusChip status={row.status} />
          <IconButton 
            size="small" 
            onClick={() => {
              setStatusEditTarget(row);
              setStatusEditOpen(true);
            }}
            sx={{ 
              color: "#6B7280", 
              "&:hover": { color: BLUE }
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      ),
    },
    {
      id: "trial", 
      label: "Trial Period", 
      minWidth: 140,
      render: (row) => <TrialCell row={row} />,
    },
    {
      id: "start_date", 
      label: "Start Date", 
      minWidth: 130,
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#6B7280", fontFamily: "DM Mono, monospace", fontSize: "12px" }}>
          {fmtDateTime(row.start_date)}
        </Typography>
      ),
    },
    {
      id: "end_date", 
      label: "End Date", 
      minWidth: 130,
      render: (row) => (
        <Typography sx={{
          ...typography.bodyLarge, fontSize: "12px",
          fontFamily: "DM Mono, monospace",
          color: row.status === "expired" ? RED : "#6B7280",
        }}>
          {fmtDateTime(row.end_date)}
        </Typography>
      ),
    },
    {
      id: "updated_at", 
      label: "Updated At", 
      minWidth: 150,
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#6B7280", fontFamily: "DM Mono, monospace", fontSize: "12px" }}>
          {fmtDateTime(row.updated_at)}
        </Typography>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* ── Page header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: "20px", fontWeight: 700, color: NAVY, fontFamily: "DM Sans, sans-serif" }}>
            Subscriptions
          </Typography>
          <Typography sx={{ ...typography.bodyLarge, color: "#6B7280", mt: 0.5 }}>
            Manage subscriber plans and trial periods
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          {/* <ButtonTwo
            variant="outlined" 
            startIcon={<RefreshIcon />}
            onClick={fetchSubscriptions}
            sx={{ borderRadius: "8px", textTransform: "none", ...typography.bodyLarge, fontWeight: 600, height: 40, borderColor: "#E5E7EB", color: "#1F2937" }}
          >
            Refresh
          </ButtonTwo> */}
          <ButtonTwo
          variant="contained"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={handleExport}
          sx={{
            backgroundColor: NAVY,
            color: "#fff",
            borderRadius: "8px",
            px: 2.5,
            py: 1,
            fontWeight: 600,
            boxShadow: "0 4px 12px rgba(8,36,92,0.25)",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#0B2F75",
              boxShadow: "0 6px 16px rgba(8,36,92,0.35)",
            },
            }}
            >
              Export
              </ButtonTwo>
        </Stack>
      </Box>

      {/* ── Stat cards ── */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <StatCard label="Total Subscribers" value={stats.total}    accentColor={BLUE}   sub="Active roster" />
        <StatCard label="Active Paid"        value={stats.active}  accentColor={GREEN}  sub="Paying subscribers" />
        <StatCard label="On Trial"           value={stats.trial}   accentColor={ORANGE} sub="In trial period" />
        <StatCard label="Cancelled / Expired" value={stats.inactive} accentColor={RED}   sub="Cancelled or expired" />
      </Stack>

      {/* ── Status tabs ── */}
      <Box sx={{ mb: 2 }}>
        <CustomTabs
          value={tabValue}
          onChange={(_, v) => { setTabValue(v); setPage(0); }}
          tabs={STATUS_TABS}
          activeBgColor={NAVY}
          sx={{ width: "fit-content" }}
        />
      </Box>

      {/* ── Filters row ── */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2.5, flexWrap: "wrap" }}>
        <TextField
          size="small" placeholder="Search by name or phone..." value={search}
          onChange={(e) => { setSearch(normalizeCombinedSearchInput(e.target.value)); setPage(0); }}
          error={Boolean(resolvedSubscriptionSearch.blockFetch && resolvedSubscriptionSearch.phoneSearchError)}
          helperText={resolvedSubscriptionSearch.blockFetch ? resolvedSubscriptionSearch.phoneSearchError : undefined}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#9CA3AF" }} />
              </InputAdornment>
            ),
            sx: { borderRadius: "8px", height: 40, ...typography.bodyLarge, minWidth: 280 },
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
        />
        {/*<AppDropdown
          menuItems={[{ label: "All Plans", value: "" }, ...PLAN_OPTIONS]}
          selectedValue={planFilter}
          onMenuSelect={(v) => { setPlanFilter(v); setPage(0); }}
        >
          {planFilter ? PLAN_OPTIONS.find(p => p.value === planFilter)?.label : "All Plans"}
        </AppDropdown>*/}
        {(search || planFilter) && (
          <Button size="small" onClick={() => { setSearch(""); setPlanFilter(""); setPage(0); }}
            sx={{ ...typography.bodyLarge, color: "#6B7280", textTransform: "none", height: 40 }}>
            Clear filters
          </Button>
        )}
      </Stack>

      {/* ── Table ── */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <Typography>Loading subscriptions...</Typography>
        </Box>
      ) : error ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      ) : (
        <AppTable
          columns={columns}
          rows={subscribers}
          rowKey="id"
          emptyMessage="No subscribers found"
          maxHeight={560}
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}

      {/* ── Edit Status Dialog ── */}
      <EditStatusDialog
        open={statusEditOpen}
        onClose={() => { setStatusEditOpen(false); setStatusEditTarget(null); }}
        onSave={handleStatusUpdate}
        currentStatus={statusEditTarget?.status || "trial"}
      />

      {/* ── Snackbar for notifications ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SubscriptionsPage;